import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
	ZkDropdownComponent,
	ZkEmployerTaxIdService,
	ZkIndividualTaxIdService,
} from '@zenklub/ng-ui';
import { ZkIOption } from '@zenklub/ng-ui/component/input/dropdown/dropdown.interface';

import {
	IProfessionalPaymentAccount,
	IProfessionalPerson,
} from '../../../interfaces/professionalprofile.interface';

@Component({
	selector: 'app-bank-data',
	templateUrl: './bank-data.component.html',
	styleUrls: ['./bank-data.component.scss'],
})
export class BankDataComponent implements OnInit, AfterViewInit {
	formParams: FormGroup;
	@Input() paymentAccount: IProfessionalPaymentAccount;
	@Input() professionalPerson: IProfessionalPerson;
	@Input() allowUpdatePaymentMethod: boolean;
	@Output() formGroup = new EventEmitter<FormGroup>();
	@ViewChild('dropdownComponent') dropdownComponent: ZkDropdownComponent;
	public keyOptions: ZkIOption[];
	constructor(
		public activatedRoute: ActivatedRoute,
		public translate: TranslateService,
		private cpfService: ZkIndividualTaxIdService,
		private cnpjService: ZkEmployerTaxIdService
	) {}

	checkProfessionalPersonBlockedEditPaymentaccount(): boolean {
		const day = new Date().getDate();
		return (
			this.professionalPerson?.blockEditPaymentAccount?.block &&
			!(
				day >=
					this.professionalPerson?.blockEditPaymentAccount?.dayStartAllowEdit &&
				day < this.professionalPerson?.blockEditPaymentAccount?.dayEndAllowEdit
			)
		);
	}

	buildAlertTextToWhenBlockEditPaymentAccount(): string {
		if (!this.professionalPerson?.blockEditPaymentAccount) {
			this.professionalPerson.blockEditPaymentAccount = {
				block: false,
				dayStartAllowEdit: 0,
				dayEndAllowEdit: 0,
			};
		}
		const { dayStartAllowEdit, dayEndAllowEdit } =
			this.professionalPerson?.blockEditPaymentAccount;
		const defaultStart = 7;
		const defaultEnd = 23;
		const startDay =
			dayStartAllowEdit > 0
				? dayStartAllowEdit.toString().padStart(2, '0')
				: defaultStart.toString().padStart(2, '0');
		const endDay =
			dayEndAllowEdit > 0
				? (dayEndAllowEdit - 1).toString().padStart(2, '0')
				: defaultEnd.toString().padStart(2, '0');
		return this.translate.instant('SESSION.BANK_DATA.BLOCK_EDIT', {
			startDay,
			endDay,
		}) as string;
	}

	checkAllowUpdatePaymentMethod(): boolean {
		return this.checkProfessionalPersonBlockedEditPaymentaccount()
			? true
			: this.allowUpdatePaymentMethod;
	}

	ngOnInit(): void {
		this.allowUpdatePaymentMethod = this.checkAllowUpdatePaymentMethod();

		if (!this.paymentAccount) {
			this.paymentAccount = {} as IProfessionalPaymentAccount;
		}
		const paymentAccount = this.paymentAccount;
		this.formParams = new FormGroup({
			pixType: new FormControl(paymentAccount.pixType),
			pixKey: new FormControl(this.paymentAccount.pixKey),
			ispb: new FormControl(this.paymentAccount.ispb),
			dontAcceptPix: new FormControl(paymentAccount.dontAcceptPix || false),
			bankName: new FormControl(paymentAccount.bankName),
			bankCode: new FormControl(paymentAccount.bankCode, Validators.required),
			bankFullName: new FormControl(
				paymentAccount.bankFullName,
				Validators.required
			),
			agency: new FormControl(paymentAccount.agency, [
				Validators.required,
				Validators.minLength(4),
				Validators.maxLength(4),
			]),
			account: new FormControl(paymentAccount.account, [
				Validators.required,
				Validators.minLength(3),
				Validators.maxLength(20),
			]),
			accountDigit: new FormControl(paymentAccount.accountDigit, [
				Validators.maxLength(2),
			]),
		});
		this.pixKeyControl(paymentAccount.pixType);
		this.setPixStatus(paymentAccount.dontAcceptPix);
		this.formParams.get('pixType').valueChanges.subscribe((value) => {
			if (!value && this.dropdownComponent) {
				this.dropdownComponent._value = '';
			}
			this.formParams.patchValue({ pixKey: '' });
			this.pixKeyControl(value);
		});

		this.formParams.get('dontAcceptPix').valueChanges.subscribe((value) => {
			this.setPixStatus(value);
		});

		this.formGroup.emit(this.formParams);
		this.buildKeyOptions();
	}

	ngAfterViewInit() {
		if (this.activatedRoute.snapshot.fragment === 'payment-content') {
			this.scrollToContent();
		}
	}

	isFieldInvalid(fieldName: string): boolean {
		const field = this.formParams?.get(fieldName);
		if (field) {
			return field.invalid && (field.dirty || field.touched);
		}
		return false;
	}

	setPixStatus(disabled: boolean) {
		const fields = ['pixType', 'pixKey'];
		if (disabled) {
			if (this.dropdownComponent) {
				this.dropdownComponent._value = '';
			}
			fields.forEach((field) => {
				const fieldControl = this.formParams.get(field);
				fieldControl.setValue('');
				fieldControl.disable();
				fieldControl.setValidators(null);
				fieldControl.updateValueAndValidity();
			});
		} else {
			const pixTypeControl = this.formParams.get('pixType');
			pixTypeControl.enable();
			pixTypeControl.setValidators(Validators.required);
			pixTypeControl.updateValueAndValidity();
			this.formParams.get('pixKey').enable();
			this.pixKeyControl(this.formParams.get('pixType').value);
		}
	}

	onSelectBank(bank) {
		if (bank) {
			this.formParams.patchValue({
				bankName: bank.name,
				bankFullName: bank.fullName,
				bankCode: bank.code.toString(),
				ispb: bank.ispb,
			});
		} else {
			this.formParams.patchValue({
				bankName: '',
				bankFullName: '',
				bankCode: '',
				ispb: '',
			});
		}
		this.formParams.markAsDirty();
		this.formParams.updateValueAndValidity();
	}

	getPixKeyError() {
		if (this.formParams.get('pixKey').value) {
			switch (this.formParams.get('pixType').value) {
				case 'cpf':
					return 'MY_ACCOUNT.FINANCIAL.BANK_DATA.FIELD_ERROR.FISCAL_ID_INVALID';
				case 'cnpj':
					return 'MY_ACCOUNT.FINANCIAL.BANK_DATA.FIELD_ERROR.BUSINESS_FISCAL_ID_INVALID';
				case 'email':
					return 'MY_ACCOUNT.FINANCIAL.BANK_DATA.FIELD_ERROR.EMAIL_INVALID';
				default:
					return 'MY_ACCOUNT.FINANCIAL.BANK_DATA.FIELD_ERROR.PIX_KEY_INVALID';
			}
		} else {
			return 'MY_ACCOUNT.FINANCIAL.BANK_DATA.FIELD_ERROR.PIX_KEY';
		}
	}

	getPixMask() {
		switch (this.formParams.get('pixType').value) {
			case 'cnpj':
				return '00.000.000/0000-00';
			case 'cpf':
				return '000.000.000-00';
			default:
				return undefined;
		}
	}

	pixKeyControl(value: string) {
		const control = this.formParams.get('pixKey');
		switch (value) {
			case 'email':
				control.setValidators([Validators.required, Validators.email]);
				break;
			case 'cpf':
				control.setValidators([
					Validators.required,
					this.validateControl(this.validateFiscalId),
				]);
				control.updateValueAndValidity();
				const fiscalId = this.professionalPerson?.professionalData?.fiscalId;
				if ((!control.value || !control.valid) && fiscalId) {
					control.setValue(fiscalId);
				}
				break;
			case 'cnpj':
				control.setValidators([
					Validators.required,
					this.validateControl(this.validateBusinessFiscalId),
				]);
				control.updateValueAndValidity();
				const businessFiscalId =
					this.professionalPerson?.professionalData?.businessFiscalId;
				if ((!control.value || !control.valid) && businessFiscalId) {
					control.setValue(businessFiscalId);
				}
				break;
			case 'random_key':
				control.setValidators([
					Validators.required,
					Validators.minLength(36),
					Validators.maxLength(36),
				]);
				break;
			default:
				control.setValidators(Validators.required);
		}
		control.updateValueAndValidity();
	}

	validateFiscalId(fiscalId: string) {
		return this.cpfService.validate(fiscalId);
	}

	validateBusinessFiscalId(fiscalId: string) {
		return this.cnpjService.validate(fiscalId);
	}

	scrollToContent() {
		document.querySelector('#payment-content')?.scrollIntoView();
	}

	private validateControl(func: CallableFunction) {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!func.bind(this)(control.value)) {
				return { invalid: true };
			}
		};
	}

	private buildKeyOptions() {
		this.keyOptions = [
			{
				label: 'CPF',
				value: 'cpf',
			},
			{
				label: 'CNPJ',
				value: 'cnpj',
			},
			{
				label: 'Email',
				value: 'email',
			},
			{
				label: 'Celular',
				value: 'phone',
			},
			{
				label: 'Chave aleatória',
				value: 'random_key',
			},
		];
	}
}
